import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { Spinner } from "../../../components/Spinner";
import {
  useBuscarPolizaMutation,
  useDatosTramiteMutation,
  useNuevoTramiteMutation,
} from "../../../services/docs";
import { useForm } from "../../../hooks/useForm";

export const NuevoTramite = () => {
  const navigate = useNavigate();

  // --- Estados y Formularios ---
  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fc_tipo_busqueda: "",
    fc_valor: "",
  });

  // Nota: Mantenemos la lógica de estos hooks aunque no se usen en el JSX de este paso
  const { formulario: uno, handleInputChange: hcUno, handleChangeAll: hca } = useForm({
    fi_cobertura_id: 0,
    fi_numero_asegurado: 0,
    fc_clave_asegurado: "",
    poliza: "",
    fecha_siniestro: "",
    fc_poliza_aseguradora: "",
  });

  const { formulario: dos, handleInputChange: hcDos, handleChangeAll: hcaDos } = useForm({
    fi_cobertura_id: 0,
    fi_numero_asegurado: 0,
    fc_clave_asegurado: "",
    fecha_siniestro: "",
    siniestro: "",
    montoReclamar: "",
    nombreContacto: "",
    telefonoContacto: "",
    correoContacto: "",
    descripcion: "",
  });

  const [buscarPolizaAction, { data: poliza, isLoading }] = useBuscarPolizaMutation();
  const [nuevoTramite] = useNuevoTramiteMutation();
  const [datosTramite] = useDatosTramiteMutation();

  const [seleccion, setSeleccion] = useState(null);
  const [seleccionSiniestro, setSeleccionSiniestro] = useState(null);

  // --- Funciones de Acción ---
  const buscarTramite = async (e?: React.FormEvent) => {
    if (e) e.preventDefault(); // Previene recarga si se usa Enter

    if (formulario.fc_tipo_busqueda === "") {
      toastr.error("Debe seleccionar un tipo de búsqueda");
      return;
    }
    if (formulario.fc_valor === "") {
      toastr.error("Debes de agregar un valor de búsqueda");
      return;
    }

    try {
      await buscarPolizaAction({
        ...formulario,
        fc_tipo_busqueda: formulario.fc_tipo_busqueda,
      }).unwrap();
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      }
    }
  };

  const verMas = (p: any) => {
    if (p.id) {
      navigate(`/home/nuevo-tramite/${p.id}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* --- Stepper Nav --- */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="flex items-center gap-3 rounded-xl border border-brand-200/70 bg-brand-50/80 px-4 py-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-500 text-xs font-bold text-white">1</span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-600">Paso 1</p>
              <p className="text-sm font-bold text-slate-900">Buscar Póliza</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-transparent px-4 py-2.5 text-slate-400">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-400">2</span>
            <div>
              <p className="text-xs font-medium text-slate-400">Paso 2</p>
              <p className="text-sm font-medium text-slate-600">Datos Asegurado</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-transparent px-4 py-2.5 text-slate-400">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-400">3</span>
            <div>
              <p className="text-xs font-medium text-slate-400">Paso 3</p>
              <p className="text-sm font-medium text-slate-600">Detalle Siniestro</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-transparent px-4 py-2.5 text-slate-400">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-xs font-semibold text-slate-400">4</span>
            <div>
              <p className="text-xs font-medium text-slate-400">Paso 4</p>
              <p className="text-sm font-medium text-slate-600">Resolución y Folio</p>
            </div>
          </div>
        </div>
      </div>

      {/* --- Contenedor de Búsqueda --- */}
      <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-slate-100 px-7 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Nuevo trámite</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Localice el contrato o póliza registrada antes de cargar las reclamaciones o evidencias del caso.
            </p>
          </div>
        </div>

        <div className="px-7 pb-2 pt-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <svg className="h-4 w-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <h2>Buscar Póliza</h2>
          </div>
        </div>

        <form onSubmit={buscarTramite} className="px-7 pb-7 pt-3">
          <div className="grid grid-cols-1 items-end gap-5 lg:grid-cols-12">

            {/* Select */}
            <div className="lg:col-span-4">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_tipo_busqueda">
                Tipo de Búsqueda
              </label>
              <div className="relative">
                <select
                  id="fc_tipo_busqueda"
                  name="fc_tipo_busqueda"
                  onChange={handleSelectChange}
                  value={formulario.fc_tipo_busqueda}
                  className="h-11 w-full cursor-pointer rounded-xl border border-slate-200 bg-slate-50/50 pl-3.5 pr-10 text-sm font-medium text-slate-800 transition-all hover:bg-slate-50 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                >
                  <option disabled value="">Selecciona criterio de búsqueda...</option>
                  <option value="poliza">Número de Póliza</option>
                  <option value="nombre">Nombre Asegurado</option>
                  <option value="rfc">RFC</option>
                </select>
              </div>
            </div>

            {/* Input */}
            <div className="lg:col-span-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600" htmlFor="fc_valor">
                Valor a buscar:
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                  </svg>
                </div>
                <input
                  id="fc_valor"
                  name="fc_valor"
                  type="text"
                  onChange={handleInputChange}
                  value={formulario.fc_valor}
                  placeholder="Ingrese el valor a buscar..."
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 text-sm font-medium text-slate-900 transition-all hover:bg-slate-50 placeholder:text-slate-400 focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2.5 lg:col-span-3">
              <button
                type="submit"
                disabled={isLoading}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 text-sm font-semibold text-white shadow-sm shadow-brand-500/25 transition-all hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow active:bg-brand-700 disabled:opacity-70"
              >
                {isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <span>Buscar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* --- Estado Vacío --- */}
      {poliza?.polizas && poliza.polizas.length === 0 && !isLoading && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-semibold">No se encontraron pólizas con los criterios proporcionados.</span>
        </div>
      )}

      {/* --- Resultados --- */}
      {poliza?.polizas && poliza.polizas.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-7 py-4">
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
              <h3 className="text-sm font-bold text-slate-900">Resultados Encontrados</h3>
              <span className="text-xs font-medium text-slate-400">({poliza.polizas.length} pólizas)</span>
            </div>
          </div>

          <div className="p-6">
            <div className="overflow-hidden rounded-xl border border-slate-200 transition-all">
              <div className="grid grid-cols-1 divide-y divide-slate-100 bg-slate-50/50 p-4 text-xs font-semibold uppercase text-slate-500 md:grid-cols-6 md:divide-x md:divide-y-0">
                <div className="col-span-2">Asegurado Titular</div>
                <div>Póliza / Producto</div>
                <div>Tipo</div>
                <div>Estado</div>
                <div className="text-right">Acción</div>
              </div>

              {poliza.polizas.map((p: any) => (
                <div key={p.id + "-" + p.fi_tipo_asegurado} className="grid grid-cols-1 items-center divide-y divide-slate-100 p-4 transition-colors hover:bg-blue-50/20 md:grid-cols-6 md:divide-x md:divide-y-0">

                  {/* Asegurado */}
                  <div className="col-span-2 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-sm font-bold text-brand-600">
                      {p.fc_nombre_asegurado?.substring(0, 2).toUpperCase() || "US"}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">{p.fc_nombre_asegurado}</div>
                      <div className="text-xs text-slate-400">RFC: {p.fc_rfc}</div>
                    </div>
                  </div>

                  {/* Póliza */}
                  <div className="py-2 md:px-4 md:py-0">
                    <div className="font-mono text-sm font-semibold text-brand-600">{p.fc_numero_poliza}</div>
                    <div className="text-xs text-slate-500">{p.fc_descripcion_producto || "Protección Integral"}</div>
                  </div>

                  {/* Tipo */}
                  <div className="py-2 md:px-4 md:py-0">
                    <span className="inline-flex items-center rounded-md bg-primary-ultra-light px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                      {p.fi_tipo_asegurado === 1 ? "Titular" : "Contratante"}
                    </span>
                  </div>

                  {/* Estado */}
                  <div className="py-2 md:px-4 md:py-0">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                      {p.fc_estatus_poliza || "Activa"}
                    </span>
                  </div>

                  {/* Acción */}
                  <div className="py-2 text-right md:py-0">
                    <button
                      onClick={() => verMas(p)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 active:bg-brand-700"
                    >
                      <span>Iniciar Trámite</span>
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
